import React from 'react';
import { Box, Typography, Container, Grid, Skeleton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useInstitutions } from '../../../hooks/useInstitutions';

const LandingAbout = () => {
  const theme = useTheme();
  const { institutions, isLoading } = useInstitutions();
  const inst = institutions?.[0] ?? null;

  const DARK_BG = theme.palette.primary.darker ?? theme.palette.primary.dark;

  return (
    <Box
      id="about"
      sx={{
        bgcolor: alpha(theme.palette.primary.lighter, 0.35),
        py: { xs: 10, md: 16 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Faint watermark */}
      <Typography
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -40, right: -20,
          fontWeight: 900,
          fontSize: { xs: '6rem', md: '14rem' },
          lineHeight: 1,
          color: alpha(theme.palette.primary.main, 0.05),
          userSelect: 'none',
          pointerEvents: 'none',
          textTransform: 'uppercase',
          letterSpacing: '-0.04em',
        }}
      >
        AGUA
      </Typography>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>

        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 2.5, fontSize: '0.7rem' }}>
          INSTITUCIÓN
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            mt: 1.5,
            mb: { xs: 8, md: 12 },
            color: 'text.primary',
            fontSize: { xs: '1.9rem', md: '2.5rem' },
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            maxWidth: 560,
          }}
        >
          {isLoading
            ? <Skeleton width="70%" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
            : (inst?.name ?? 'Nuestra Institución')}
        </Typography>

        <Grid container spacing={{ xs: 6, md: 8 }}>

          {/* Mission — white card */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              p: { xs: 4, md: 5 },
              bgcolor: '#FFFFFF',
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: alpha(theme.palette.primary.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FormatQuoteIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                </Box>
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: 2, fontSize: '0.68rem' }}>
                  MISIÓN
                </Typography>
              </Box>

              {isLoading ? (
                <>
                  {[100, 95, 80, 70].map((w, i) => (
                    <Skeleton key={i} variant="text" width={`${w}%`} height={22} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }} />
                  ))}
                </>
              ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.9, fontSize: '0.97rem', flexGrow: 1, fontStyle: inst?.mission ? 'normal' : 'italic' }}>
                  {inst?.mission ?? 'Información de misión no registrada.'}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Vision — dark card */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              p: { xs: 4, md: 5 },
              bgcolor: DARK_BG,
              borderRadius: '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Subtle glow inside dark card */}
              <Box sx={{
                position: 'absolute', top: 0, right: 0,
                width: '60%', height: '60%',
                background: `radial-gradient(ellipse at top right, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 70%)`,
                pointerEvents: 'none',
              }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: alpha(theme.palette.primary.light, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FormatQuoteIcon sx={{ color: 'primary.light', fontSize: 20 }} />
                </Box>
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'primary.light', letterSpacing: 2, fontSize: '0.68rem' }}>
                  VISIÓN
                </Typography>
              </Box>

              {isLoading ? (
                <>
                  {[100, 90, 75].map((w, i) => (
                    <Skeleton key={i} variant="text" width={`${w}%`} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.07)' }} />
                  ))}
                </>
              ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.9, fontSize: '0.97rem', flexGrow: 1, fontStyle: inst?.vision ? 'normal' : 'italic', position: 'relative', zIndex: 1 }}>
                  {inst?.vision ?? 'Información de visión no registrada.'}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* whatDo — full width, only if data exists */}
          {(isLoading || inst?.whatDo) && (
            <Grid item xs={12}>
              <Box sx={{
                p: { xs: 4, md: 5 },
                bgcolor: '#FFFFFF',
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: { xs: 3, md: 5 },
                alignItems: { xs: 'flex-start', md: 'center' },
                flexDirection: { xs: 'column', md: 'row' },
              }}>
                {/* Label */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: 'primary.main',
                    flexShrink: 0,
                    minWidth: { md: 180 },
                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                  }}
                >
                  ¿Qué hacemos?
                </Typography>

                {/* Divider: vertical on md, horizontal on xs */}
                <Box sx={{
                  flexShrink: 0,
                  width: { xs: '100%', md: '1px' },
                  height: { xs: '1px', md: 40 },
                  bgcolor: 'divider',
                  alignSelf: { md: 'center' },
                }} />

                {/* Content */}
                {isLoading ? (
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="100%" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }} />
                    <Skeleton variant="text" width="85%"  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.85, fontSize: '0.97rem', flex: 1 }}>
                    {inst.whatDo}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingAbout;
